import type { JSX, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Standardized empty state — every page that loads zero rows uses
 * this so the visual language stays consistent.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '40px 20px',
        borderRadius: 16,
        background: 'var(--bg-overlay)',
        border: '1px dashed var(--border)',
        textAlign: 'center',
      }}
    >
      {Icon && (
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'rgba(148,163,184,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 4,
          }}
        >
          <Icon width={22} height={22} color="var(--text-muted)" />
        </div>
      )}
      <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
        {title}
      </p>
      {description && (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-muted)', maxWidth: 380 }}>
          {description}
        </p>
      )}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}

/**
 * Skeleton primitives — rectangle with shimmer animation. Use as
 * loading placeholder for any element with a known shape.
 */
export function Skeleton({
  className,
  width,
  height = 16,
  rounded = 6,
}: {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: number;
}): JSX.Element {
  return (
    <div
      className={className}
      style={{
        width: width ?? '100%',
        height,
        borderRadius: rounded,
        background:
          'linear-gradient(90deg, rgba(148,163,184,0.05) 0%, rgba(148,163,184,0.18) 40%, rgba(148,163,184,0.05) 80%)',
        backgroundSize: '200% 100%',
        animation: 'admin-shimmer 1.6s linear infinite',
      }}
    />
  );
}

/**
 * Row-shaped skeleton for table loading. N rows with the given column
 * count.
 */
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} style={{ display: 'flex', gap: 12 }}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} height={28} width={c === 0 ? '20%' : c === cols - 1 ? '15%' : '25%'} />
          ))}
        </div>
      ))}
    </div>
  );
}
